/**
 * MAP CONTROLLER (owner B2): creates the MapLibre map and implements MapController (contracts/map.ts).
 *
 *   const ctrl = createController(el, { reducedMotion: () => getApp().reducedMotion });
 *   await ctrl.ready;   // style + anchors + terrain + light/overlays/markers attached, "map:ready" emitted
 *
 * Lifecycle. Created only after preload.essential (the data rule), so store.data/routes/geo exist.
 * destroy() is idempotent and leak-free (StrictMode mounts twice).
 *
 * Moods (§E.8). setMood() never calls setStyle: it builds the next style and applies the paint diff with
 * setPaintProperty (a `-transition` of the mood duration on each changed colour). It asserts the diff
 * adds or removes nothing, so ld-* layers, the anchors and the custom LightLayer always survive. Hillshade
 * direction and exaggeration tween on the ticker (terrain.ts). MOODS[m].cssVars go on the container.
 *
 * The controller follows the store by itself (idempotently): mood (deferred a microtask, so an explicit
 * setMood(m, { duration }) in the same tick wins, e.g. the 2400 ms sunrise), layers, layout and insets.
 *
 * Camera (§D.11, §E.11). easeTo / flyTo resolve "done" when the move completes, "interrupted" when user
 * input took over during it, "cancelled" when o.signal aborted it or another move / stop() replaced it.
 * Padding = the published insets merged with target.padding. Under reduced motion every move is at most
 * 240 ms, and a move farther than one viewport jumps (§E.10).
 *
 * Input. Rotation, pitch gestures, box zoom, MapLibre's keyboard and double-click zoom are off. Replaced by:
 * wheel rate 1/600; double-click / double-tap = easeTo zoom + 1 around the pointer (420 settle, 160
 * reduced); arrows pan 100 px, + / − zoom by 1 (420 settle). Every user input on the map emits
 * bus "camera:interrupt" { reason: "user" } and calls the onInteract listeners; a press on the map
 * stops a controller move at once.
 */
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
// ?worker&url: Vite bundles the ES worker with its shared chunk (a plain ?url copy would miss ./maplibre-gl-shared.mjs in a build).
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import type { Map as MLMap, MapMouseEvent, PaddingOptions, StyleSpecification } from "maplibre-gl";
import type { LngLat, SiteCollection } from "@/contracts/data";
import {
  ANCHORS,
  SOURCE_IDS,
  type CameraTarget,
  type LightLayerAPI,
  type MapController,
  type MarkersAPI,
  type MoveOpts,
  type MoveResult,
  type OverlaysAPI,
} from "@/contracts/map";
import type { Insets, LayerFlags, Layout, Mood } from "@/contracts/store";
import { REGION_BBOX } from "@/contracts/taxonomy";
import { DUR, EASE, easeBy } from "@/design";
import { createLightLayer } from "@/map/gl";
import { createOverlays } from "@/map/layers";
import { createMarkers } from "@/map/markers";
import { prewarmTiles } from "@/map/prewarm";
import { MOODS, MOOD_TRANSITION_MS } from "@/map/style/moods";
import { bus, on } from "@/store/bus";
import { actions, appStore, getApp } from "@/store/store";
import { buildStyle, diffStyles, isTransitionable, labelLayerIds, type StyleOp } from "./style/build";
import { createTerrain, type Terrain } from "./terrain";

/* ═══ Pure camera math (exported for tests and for B4/B5) ════════════════ */

export const EARTH_CIRCUMFERENCE_M = 40075016.686;
const TILE = 512;
const RAD = Math.PI / 180;

/** Metres per CSS pixel at a latitude and (512 px tile) zoom. At the site: 74 143 / 2^z. */
export function metresPerPixel(lat: number, zoom: number): number {
  return (EARTH_CIRCUMFERENCE_M * Math.cos(lat * RAD)) / (TILE * 2 ** zoom);
}

/** The zoom at which a circle of `radiusM` fits the smaller side of a frameW × frameH frame. */
export function zoomForRadius(radiusM: number, lat: number, frameW: number, frameH: number): number {
  const side = Math.max(1, Math.min(frameW, frameH));
  return Math.log2((EARTH_CIRCUMFERENCE_M * Math.cos(lat * RAD) * side) / (TILE * 2 * radiusM));
}

/** Web-mercator x, y in 0…1 world units. */
export function toMercator([lng, lat]: LngLat): [number, number] {
  const s = Math.sin(lat * RAD);
  return [(lng + 180) / 360, 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)];
}
export function fromMercator([x, y]: [number, number]): LngLat {
  const lng = x * 360 - 180;
  const lat = (360 / Math.PI) * Math.atan(Math.exp((180 - y * 360) * RAD)) - 90;
  return [lng, lat];
}

export type BBox4 = readonly [number, number, number, number];

/** fitBounds without a map: centre (mercator midpoint) and zoom for a bbox inside w × h with `pad` px. */
export function fitBBox(b: BBox4, w: number, h: number, pad = 0): { center: LngLat; zoom: number } {
  const [x0, y1] = toMercator([b[0], b[1]]);
  const [x1, y0] = toMercator([b[2], b[3]]);
  const zx = Math.log2(Math.max(1, w - 2 * pad) / ((x1 - x0) * TILE));
  const zy = Math.log2(Math.max(1, h - 2 * pad) / ((y1 - y0) * TILE));
  return { center: fromMercator([(x0 + x1) / 2, (y0 + y1) / 2]), zoom: Math.min(zx, zy) };
}

/** The lowest zoom at which a w × h viewport fits inside the bbox (what maxBounds enforces). */
export function minZoomInside(b: BBox4, w: number, h: number): number {
  const [x0, y1] = toMercator([b[0], b[1]]);
  const [x1, y0] = toMercator([b[2], b[3]]);
  return Math.max(Math.log2(w / ((x1 - x0) * TILE)), Math.log2(h / ((y1 - y0) * TILE)));
}

/** Moves a centre so a w × h viewport at `zoom` stays inside the bbox. */
export function clampCenter(c: LngLat, zoom: number, b: BBox4, w: number, h: number): LngLat {
  const size = TILE * 2 ** zoom;
  const [x0, y1] = toMercator([b[0], b[1]]);
  const [x1, y0] = toMercator([b[2], b[3]]);
  const hw = w / 2 / size;
  const hh = h / 2 / size;
  const [x, y] = toMercator(c);
  const cx = x1 - x0 < 2 * hw ? (x0 + x1) / 2 : Math.min(Math.max(x, x0 + hw), x1 - hw);
  const cy = y1 - y0 < 2 * hh ? (y0 + y1) / 2 : Math.min(Math.max(y, y0 + hh), y1 - hh);
  return fromMercator([cx, cy]);
}

/** Shrinks insets proportionally so at least `minFrame` px of map stays between them. */
export function safeInsets(i: Insets, w: number, h: number, minFrame = 96): Insets {
  const fit = (a: number, b: number, total: number): [number, number] => {
    const room = Math.max(0, total - minFrame);
    const sum = Math.max(0, a) + Math.max(0, b);
    if (sum <= room || sum === 0) return [Math.max(0, a), Math.max(0, b)];
    const k = room / sum;
    return [Math.max(0, a) * k, Math.max(0, b) * k];
  };
  const [left, right] = fit(i.left, i.right, w);
  const [top, bottom] = fit(i.top, i.bottom, h);
  return { top, right, bottom, left };
}

/**
 * Pole of inaccessibility (the polylabel algorithm): the interior point farthest from the outline, in a
 * local metric frame. `precisionM` stops the search once no cell can beat the best by more than that.
 */
export function polylabel(rings: LngLat[][], precisionM = 2): LngLat {
  const outer = rings[0];
  if (!outer || outer.length < 3) return outer?.[0] ?? [0, 0];
  const lat0 = outer.reduce((s, p) => s + p[1], 0) / outer.length;
  const kx = 111320 * Math.cos(lat0 * RAD);
  const ky = 110574;
  const P = rings.map((r) => r.map(([x, y]) => [x * kx, y * ky] as [number, number]));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of P[0]) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  const dist = (x: number, y: number): number => {
    let inside = false;
    let best = Infinity;
    for (const ring of P) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [ax, ay] = ring[i];
        const [bx, by] = ring[j];
        if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
        // squared distance to segment
        let dx = bx - ax;
        let dy = by - ay;
        let px = ax;
        let py = ay;
        if (dx !== 0 || dy !== 0) {
          const t = ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy);
          if (t > 1) {
            px = bx;
            py = by;
          } else if (t > 0) {
            px += dx * t;
            py += dy * t;
          }
        }
        dx = x - px;
        dy = y - py;
        best = Math.min(best, dx * dx + dy * dy);
      }
    }
    return (inside ? 1 : -1) * Math.sqrt(best);
  };
  interface Cell {
    x: number;
    y: number;
    h: number;
    d: number;
    max: number;
  }
  const cell = (x: number, y: number, h: number): Cell => {
    const d = dist(x, y);
    return { x, y, h, d, max: d + h * Math.SQRT2 };
  };
  const size = Math.min(maxX - minX, maxY - minY);
  if (size <= 0) return outer[0];
  const queue: Cell[] = [];
  const h0 = size / 2;
  for (let x = minX; x < maxX; x += size) for (let y = minY; y < maxY; y += size) queue.push(cell(x + h0, y + h0, h0));
  // centroid seed
  let best = cell((minX + maxX) / 2, (minY + maxY) / 2, 0);
  let guard = 0;
  while (queue.length && guard++ < 20000) {
    let mi = 0;
    for (let i = 1; i < queue.length; i++) if (queue[i].max > queue[mi].max) mi = i;
    const c = queue.splice(mi, 1)[0];
    if (c.d > best.d) best = c;
    if (c.max - best.d <= precisionM) continue;
    const h = c.h / 2;
    queue.push(cell(c.x - h, c.y - h, h), cell(c.x + h, c.y - h, h), cell(c.x - h, c.y + h, h), cell(c.x + h, c.y + h, h));
  }
  return [Math.round((best.x / kx) * 1e6) / 1e6, Math.round((best.y / ky) * 1e6) / 1e6];
}

/** The site's label point (spec §D.4): polylabel of the site polygon, else the origin, else `fallback`. */
export function siteLabelPoint(site: SiteCollection | null | undefined, fallback: LngLat): LngLat {
  const poly = site?.features.find((f) => f.geometry.type === "Polygon");
  if (poly && poly.geometry.type === "Polygon") return polylabel(poly.geometry.coordinates as LngLat[][]);
  const origin = site?.features.find((f) => f.properties.id === "origin");
  if (origin && origin.geometry.type === "Point") return origin.geometry.coordinates as LngLat;
  return fallback;
}

/** Navi Mumbai airport → Purandar (§D.11 regionTarget). */
export const REGION_VIEW: BBox4 = [73.0, 18.25, 74.2, 19.05];
/** The ring the home view fits inside the inset frame (§D.11). */
export const HOME_RING_M = 10000;
export const MIN_ZOOM = 8;
export const MAX_ZOOM = 16;
export const MAX_PITCH = 60;
/** After the intro's "done" beat, the region's parent tiles pre-warm (L2-09). */
const PREWARM_AFTER_DONE_MS = 800;

/* ═══ Controller ══════════════════════════════════════════════════════════ */

export interface ControllerOptions {
  pixelRatio?: number;
  reducedMotion: () => boolean;
  lightMode?: "auto" | "gl" | "fallback";
  /** Initial mood (default: the store's). The intro may start at night: pass "night" or setMood(…, {duration: 0}). */
  mood?: Mood;
  /** Map text layout (default: the store's; kiosk → × 1.2, §D.12). */
  layout?: Layout;
  /** Initial camera (default: homeTarget()). */
  initial?: CameraTarget;
}

export interface ControllerDebug {
  mood: Mood;
  moving: boolean;
  zoom: number;
  center: LngLat;
  pitch: number;
  bearing: number;
  insets: Insets;
  demAttached: boolean;
  lastMood: { ops: number; paintOnly: boolean; structural: string[] } | null;
  lastMove: MoveResult | null;
  interrupts: number;
}

/** MapController plus a few non-contract helpers (B4/B6 may use them through this type). */
export interface ControllerHandle extends MapController {
  /** The site's label point (camera centre for home; the Whorl's position, §D.4). */
  siteCenter(): LngLat;
  /** Fade every contour layer to v (0…1): the intro's "contours" beat (§E.7, 2400 drift). */
  fadeContours(v: number, o?: { duration?: number; ease?: MoveOpts["ease"] }): Promise<boolean>;
  debug(): ControllerDebug;
}

const ZERO: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
const FLAG_KEYS: (keyof LayerFlags)[] = ["routes", "future", "contours", "hillshade", "rings", "boundary", "labels"];
const CANVAS_LABEL = "Use arrow keys to pan, plus and minus to zoom.";

let workerSet = false;

interface Move {
  id: number;
  user: boolean;
  reached: boolean;
  cancelled: boolean;
  settled: boolean;
  finish: () => void;
}

/* No-op stand-ins when a module fails to attach: the rest of the map keeps working. */
const noopLight = (id: string): LightLayerAPI => ({
  id,
  mode: "fallback",
  setPaths: () => {},
  setState: () => {},
  getState: () => undefined,
  setPalette: () => {},
  pointAt: () => null,
  requestRender: () => {},
  onModeChange: () => () => {},
  destroy: () => {},
});
const noopMarkers = (): MarkersAPI => {
  const n = () => {};
  return { setPlaces: n, setFocusSet: n, setSelected: n, setHovered: n, setDimmed: n, arrive: n, pulse: n, setSite: n, setMood: n, destroy: n };
};
const noopOverlays = (): OverlaysAPI => {
  const n = () => {};
  return { setBoundary: n, setRings: async () => {}, setRoads: n, setFuture: n, setVeil: n, lightContours: n, setMood: n, destroy: n };
};

export function createController(container: HTMLElement, o: ControllerOptions): ControllerHandle {
  if (!workerSet) {
    maplibregl.setWorkerUrl(workerUrl);
    workerSet = true;
  }
  const reduced = (): boolean => {
    try {
      return !!o.reducedMotion();
    } catch {
      return false;
    }
  };

  const app0 = getApp();
  let mood: Mood = o.mood ?? app0.mood;
  let layout: Layout = o.layout ?? app0.layout;
  const flags: LayerFlags = { ...app0.layers };
  let insets: Insets = { ...app0.insets };
  const site = siteLabelPoint(app0.geo?.site, app0.data?.origin.coord ?? [73.960862, 18.605185]);

  let destroyed = false;
  let styleReady = false;
  /** The style the map should show (flags, mood, layout baked in). */
  let current: StyleSpecification = build(mood);
  /** The style the map actually holds (differs from `current` only before "style.load"). */
  let applied: StyleSpecification = current;
  let lastMood: ControllerDebug["lastMood"] = null;
  let lastMove: MoveResult | null = null;
  let interrupts = 0;

  let light: LightLayerAPI | null = null;
  let overlays: OverlaysAPI | null = null;
  let markers: MarkersAPI | null = null;
  let terrain: Terrain | null = null;

  function build(m: Mood): StyleSpecification {
    return buildStyle(m, { layout, contours: getApp().geo?.contours ?? null, flags: { labels: flags.labels, contours: flags.contours } });
  }

  /* The container's size, cached (L2-06b): the drive calls jumpTo every frame and each call pads the
     camera by this size, so it must not read layout (a forced reflow whenever the HUD has just changed).
     A ResizeObserver refreshes it; before its first report it is measured once. */
  let sized: { w: number; h: number } | null = null;
  const measure = (): { w: number; h: number } => ({
    w: container.clientWidth || (typeof window !== "undefined" ? window.innerWidth : 1440),
    h: container.clientHeight || (typeof window !== "undefined" ? window.innerHeight : 900),
  });
  const size = (): { w: number; h: number } => (sized ??= measure());
  const sizeRo =
    typeof ResizeObserver !== "undefined"
      ? new ResizeObserver((entries) => {
          const r = entries[entries.length - 1]?.contentRect;
          sized = r && r.width > 0 && r.height > 0 ? { w: Math.round(r.width), h: Math.round(r.height) } : null;
        })
      : null;
  sizeRo?.observe(container);

  /* ── targets ──────────────────────────────────────────────────────── */

  function homeTarget(): CameraTarget {
    const { w, h } = size();
    const s = safeInsets(insets, w, h);
    const zoom = zoomForRadius(HOME_RING_M, site[1], w - s.left - s.right, h - s.top - s.bottom);
    return { center: site, zoom: Math.round(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom)) * 1000) / 1000, bearing: 0, pitch: 0 };
  }

  function regionTarget(): CameraTarget {
    const { w, h } = size();
    const fit = fitBBox(REGION_VIEW, w, h, 48);
    const zoom = Math.max(fit.zoom, minZoomInside(REGION_BBOX, w, h) + 0.001);
    const center = clampCenter(fit.center, zoom, REGION_BBOX, w, h);
    return { center, zoom: Math.round(zoom * 1000) / 1000, bearing: 0, pitch: 0, padding: { ...ZERO } };
  }

  /* ── the map ──────────────────────────────────────────────────────── */

  const init = o.initial ?? homeTarget();
  const map: MLMap = new maplibregl.Map({
    container,
    style: current,
    center: init.center,
    zoom: init.zoom,
    bearing: init.bearing ?? 0,
    pitch: init.pitch ?? 0,
    ...(o.pixelRatio ? { pixelRatio: o.pixelRatio } : {}),
    canvasContextAttributes: { antialias: false, powerPreference: "high-performance" },
    maxBounds: [
      [REGION_BBOX[0], REGION_BBOX[1]],
      [REGION_BBOX[2], REGION_BBOX[3]],
    ],
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    maxPitch: MAX_PITCH,
    attributionControl: false,
    // room in each source's tile cache for the pre-warmed region parents (L2-09) beside what the visitor explores
    maxTileCacheZoomLevels: 8,
    fadeDuration: 240,
    dragRotate: false,
    pitchWithRotate: false,
    touchPitch: false,
    keyboard: false,
    doubleClickZoom: false,
    boxZoom: false,
    renderWorldCopies: false,
    refreshExpiredTiles: false,
    validateStyle: import.meta.env?.DEV ?? true,
    locale: { "Map.Title": CANVAS_LABEL },
  });
  map.touchZoomRotate.disableRotation();
  map.scrollZoom.setWheelZoomRate(1 / 600);
  map.jumpTo({ ...init, padding: paddingFor(init) });

  /* L2-09: once the opening has played, the region's parent tiles (basemap and DEM, z9–10) load in the
     background into MapLibre's tile cache, so the first long flight never shows a blank basemap. */
  const prewarmAc = new AbortController();
  let prewarmTimer: ReturnType<typeof setTimeout> | null = null;
  const offPrewarm = on("intro:beat", ({ beat }) => {
    if (beat !== "done" || prewarmTimer || prewarmAc.signal.aborted) return;
    prewarmTimer = setTimeout(() => {
      if (destroyed) return;
      void prewarmTiles(map, { sources: [SOURCE_IDS.basemap, SOURCE_IDS.dem], zooms: [9, 10], bbox: REGION_BBOX, signal: prewarmAc.signal });
    }, PREWARM_AFTER_DONE_MS);
  });

  const canvas = map.getCanvas();
  const canvasBox = map.getCanvasContainer();
  canvas.setAttribute("role", "application");
  canvas.setAttribute("aria-label", CANVAS_LABEL);
  container.classList.add("ld-loc-map-host");

  // Edge scrim between the canvas and the DOM pins (pins are appended to the canvas container later).
  const vignette = document.createElement("div");
  vignette.className = "ld-loc-map-vignette";
  vignette.setAttribute("aria-hidden", "true");
  canvas.insertAdjacentElement("afterend", vignette);

  applyCssVars(mood);

  function applyCssVars(m: Mood): void {
    for (const [k, v] of Object.entries(MOODS[m].cssVars)) container.style.setProperty(k, v);
    container.dataset.mood = m;
  }

  map.on("error", (ev) => {
    if (destroyed) return;
    const e = ev as unknown as { error?: Error };
    console.warn("[map]", e.error?.message ?? e);
  });

  /* ── ready ────────────────────────────────────────────────────────── */

  let resolveReady!: () => void;
  let rejectReady!: (e: unknown) => void;
  const ready = new Promise<void>((res, rej) => {
    resolveReady = res;
    rejectReady = rej;
  });
  ready.catch(() => {}); // awaiting callers still see the rejection; this only silences "unhandled"

  const onStyleError = (ev: object) => {
    const e = ev as { error?: Error; sourceId?: string; tile?: unknown };
    if (styleReady || destroyed || e.sourceId || e.tile) return;
    rejectReady(e.error ?? new Error("The map style could not load."));
  };
  map.on("error", onStyleError);

  map.once("style.load", () => {
    if (destroyed) return;
    try {
      attach();
      styleReady = true;
      // Anything changed before the style loaded (mood, flags, layout) lands now, without transitions.
      if (current !== applied) restyle(current, 0);
      map.off("error", onStyleError);
      resolveReady();
      bus.emit("map:ready");
    } catch (err) {
      rejectReady(err);
    }
  });

  function attach(): void {
    for (const id of Object.values(ANCHORS)) if (!map.getLayer(id)) throw new Error(`map: anchor ${id} is missing`);
    terrain = createTerrain(map, { mood, reducedMotion: reduced, contours: flags.contours, hillshade: flags.hillshade });
    try {
      light = createLightLayer(map, { beforeId: ANCHORS.routes, reducedMotion: reduced, mode: o.lightMode ?? "auto" });
    } catch (err) {
      console.error("[map] the light layer did not attach", err);
      light = noopLight("ld-light");
    }
    try {
      overlays = createOverlays(map, { beforeId: ANCHORS.overlays, placesBeforeId: ANCHORS.places, reducedMotion: reduced });
    } catch (err) {
      console.error("[map] the overlays did not attach", err);
      overlays = noopOverlays();
    }
    try {
      markers = createMarkers(map, { beforeId: ANCHORS.places, topId: ANCHORS.top, reducedMotion: reduced });
    } catch (err) {
      console.error("[map] the markers did not attach", err);
      markers = noopMarkers();
    }
    // The controller's mood may differ from the store's (the intro starts at night): align the modules.
    if (mood !== getApp().mood) {
      light.setPalette(MOODS[mood].light);
      overlays.setMood(mood);
      markers.setMood(mood);
    }
  }

  /* ── style diffs ──────────────────────────────────────────────────── */

  // Style keys computed at runtime (diff ops): MapLibre's typed setters want literal key unions.
  type LooseSetters = { setPaintProperty(l: string, k: string, v: unknown): unknown; setLayoutProperty(l: string, k: string, v: unknown): unknown };
  const loose = map as unknown as LooseSetters;
  const setPaint = (layer: string, key: string, value: unknown): void => {
    loose.setPaintProperty(layer, key, value);
  };
  const setLayout = (layer: string, key: string, value: unknown): void => {
    loose.setLayoutProperty(layer, key, value);
  };

  function applyOps(ops: StyleOp[], duration: number): void {
    const tr = { duration, delay: 0 };
    for (const op of ops) {
      if (!map.getLayer(op.layer)) continue;
      try {
        if (op.kind === "paint" && op.key) {
          if (isTransitionable(op.key)) setPaint(op.layer, `${op.key}-transition`, tr);
          setPaint(op.layer, op.key, op.value);
        } else if (op.kind === "layout" && op.key) {
          setLayout(op.layer, op.key, op.value);
        } else if (op.kind === "filter") {
          map.setFilter(op.layer, op.value as maplibregl.FilterSpecification | null);
        } else if (op.kind === "zoom") {
          const [mn, mx] = op.value as [number | undefined, number | undefined];
          map.setLayerZoomRange(op.layer, mn ?? 0, mx ?? 24);
        }
      } catch (err) {
        console.warn("[map] style op failed", op.layer, op.key, err);
      }
    }
  }

  /** Moves the map to `next` with paint/layout ops only. Returns the diff summary. */
  function restyle(next: StyleSpecification, duration: number): { ops: number; paintOnly: boolean; structural: string[] } {
    current = next;
    if (!styleReady) return { ops: 0, paintOnly: true, structural: [] };
    const d = diffStyles(applied, next);
    if (d.structural.length) console.error("[map] a restyle would add, remove or reorder layers; those changes were skipped:", d.structural);
    applyOps(d.ops, duration);
    applied = next;
    return { ops: d.ops.length, paintOnly: d.ops.every((x) => x.kind === "paint"), structural: d.structural };
  }

  /* ── layer flags ──────────────────────────────────────────────────── */

  let labelTimer: ReturnType<typeof setTimeout> | null = null;
  function setLabels(v: boolean): void {
    const r = reduced();
    const dIn = r ? 160 : DUR.calm;
    const dOut = r ? 160 : Math.round(DUR.calm * 0.7);
    const ids = labelLayerIds(current);
    if (labelTimer) clearTimeout(labelTimer);
    labelTimer = null;
    for (const id of ids) {
      if (!map.getLayer(id)) continue;
      if (v) map.setLayoutProperty(id, "visibility", "visible");
      map.setPaintProperty(id, "text-opacity-transition", { duration: v ? dIn : dOut, delay: 0 });
      map.setPaintProperty(id, "text-opacity", v ? 1 : 0);
    }
    // Hidden labels stop taking collision space once they have faded.
    if (!v)
      labelTimer = setTimeout(() => {
        labelTimer = null;
        if (destroyed || flags.labels) return;
        for (const id of ids) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", "none");
      }, dOut + 40);
  }

  function applyFlag(k: keyof LayerFlags, v: boolean): void {
    if (flags[k] === v) return;
    flags[k] = v;
    if (!styleReady) {
      current = build(mood);
      return;
    }
    switch (k) {
      case "labels":
        setLabels(v);
        current = applied = build(mood);
        break;
      case "contours":
        terrain?.setContoursVisible(v);
        current = applied = build(mood);
        break;
      case "hillshade":
        terrain?.setHillshadeVisible(v);
        break;
      case "rings":
        void overlays?.setRings({ visible: v });
        break;
      case "boundary":
        overlays?.setBoundary({ visible: v });
        break;
      case "future":
        overlays?.setFuture({ visible: v });
        break;
      case "routes":
        break; // the LightLayer follows store.layers.routes itself (B3)
    }
  }

  /* ── camera ───────────────────────────────────────────────────────── */

  let moveSeq = 0;
  let currentMove: Move | null = null;
  const pending = new Set<Move>();

  function paddingFor(t: CameraTarget): PaddingOptions {
    const { w, h } = size();
    return safeInsets({ ...insets, ...(t.padding ?? {}) }, w, h);
  }

  function isFar(t: CameraTarget): boolean {
    const { w, h } = size();
    const a = map.project(map.getCenter());
    const b = map.project(t.center as maplibregl.LngLatLike);
    return Math.hypot(a.x - b.x, a.y - b.y) > Math.max(w, h) || Math.abs(t.zoom - map.getZoom()) > 2;
  }

  function jumpTo(t: CameraTarget): void {
    if (destroyed) return;
    map.jumpTo({
      center: t.center as maplibregl.LngLatLike,
      zoom: t.zoom,
      bearing: t.bearing ?? map.getBearing(),
      pitch: t.pitch ?? map.getPitch(),
      padding: paddingFor(t),
    });
  }

  function move(kind: "ease" | "fly", t: CameraTarget, mo: MoveOpts & { curve?: number } = {}): Promise<MoveResult> {
    if (destroyed || mo.signal?.aborted) return Promise.resolve("cancelled");
    let duration = Math.max(0, mo.duration ?? DUR.scene);
    if (reduced()) {
      if (isFar(t)) {
        jumpTo(t);
        lastMove = "done";
        return Promise.resolve("done");
      }
      duration = Math.min(duration, 240);
      kind = "ease";
    }
    const id = ++moveSeq;
    return new Promise<MoveResult>((resolve) => {
      const mv: Move = { id, user: false, reached: duration <= 0, cancelled: false, settled: false, finish: () => {} };
      const ease = easeBy(mo.ease ?? "drift");
      const easing = (x: number): number => {
        if (x >= 1) mv.reached = true;
        return ease(x);
      };
      const onEnd = (e: object) => {
        if ((e as { ldMove?: number }).ldMove === id) finish();
      };
      const onAbort = () => {
        if (mv.settled) return;
        mv.cancelled = true;
        if (currentMove === mv) map.stop();
        finish();
      };
      function finish(): void {
        if (mv.settled) return;
        mv.settled = true;
        map.off("moveend", onEnd);
        mo.signal?.removeEventListener("abort", onAbort);
        pending.delete(mv);
        if (currentMove === mv) currentMove = null;
        const r: MoveResult = mv.cancelled ? "cancelled" : mv.reached ? "done" : mv.user ? "interrupted" : "cancelled";
        lastMove = r;
        resolve(r);
      }
      mv.finish = finish;
      pending.add(mv);
      map.on("moveend", onEnd);
      mo.signal?.addEventListener("abort", onAbort, { once: true });
      currentMove = mv; // set before the call: starting a move stops the previous one synchronously
      const cam = {
        center: t.center as maplibregl.LngLatLike,
        zoom: t.zoom,
        bearing: t.bearing ?? map.getBearing(),
        pitch: t.pitch ?? map.getPitch(),
        padding: paddingFor(t),
        duration,
        easing,
        essential: true, // our own reduced-motion policy applies; MapLibre must not zero the duration
      };
      if (kind === "fly") map.flyTo({ ...cam, curve: mo.curve ?? 1.42 }, { ldMove: id });
      else map.easeTo(cam, { ldMove: id });
    });
  }

  // "camera:settled": after any move, once nothing else has started moving.
  const onMoveEnd = () => {
    queueMicrotask(() => {
      if (!destroyed && !map.isMoving() && !currentMove) bus.emit("camera:settled");
    });
  };
  map.on("moveend", onMoveEnd);

  /* ── input ────────────────────────────────────────────────────────── */

  const interactCbs = new Set<() => void>();
  let lastEmit = 0;
  function userInput(o2: { stop: boolean; throttle?: boolean }): void {
    if (destroyed) return;
    if (currentMove) {
      currentMove.user = true;
      if (o2.stop) map.stop();
    }
    const now = performance.now();
    if (o2.throttle && now - lastEmit < 100) return;
    lastEmit = now;
    interrupts++;
    bus.emit("camera:interrupt", { reason: "user" });
    for (const cb of [...interactCbs]) {
      try {
        cb();
      } catch (err) {
        console.error("[map] onInteract listener", err);
      }
    }
  }
  // Another surface (the HUD's zoom buttons) announces user input before it moves the camera.
  const offBusInterrupt = on("camera:interrupt", (e) => {
    if (e.reason === "user" && currentMove) currentMove.user = true;
  });

  const zoomAround = (at: maplibregl.LngLat, dz: number, tag: string) => {
    const z = Math.min(map.getMaxZoom(), Math.max(map.getMinZoom(), map.getZoom() + dz));
    map.easeTo({ zoom: z, around: at, duration: reduced() ? 160 : DUR.calm, easing: EASE.settle, essential: true }, { ldUser: tag });
  };

  const touches = new Set<number>();
  let tapDown: { x: number; y: number; t: number } | null = null;
  let lastTap: { x: number; y: number; t: number } | null = null;
  let lastDoubleTap = 0;

  const onPointerDown = (e: PointerEvent) => {
    if (e.target !== canvas) return;
    if (e.pointerType === "touch") {
      touches.add(e.pointerId);
      tapDown = touches.size === 1 ? { x: e.clientX, y: e.clientY, t: e.timeStamp } : null;
    }
    if (e.pointerType === "mouse" && e.button !== 0) return;
    userInput({ stop: true });
  };
  const onPointerUp = (e: PointerEvent) => {
    if (e.pointerType !== "touch") return;
    const wasSingle = touches.size === 1;
    touches.delete(e.pointerId);
    const d = tapDown;
    tapDown = null;
    if (!wasSingle || !d || e.target !== canvas) return;
    if (e.timeStamp - d.t > 260 || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 10) {
      lastTap = null;
      return;
    }
    const tap = { x: e.clientX, y: e.clientY, t: e.timeStamp };
    if (lastTap && tap.t - lastTap.t < 320 && Math.hypot(tap.x - lastTap.x, tap.y - lastTap.y) < 32) {
      lastTap = null;
      lastDoubleTap = performance.now();
      const r = canvas.getBoundingClientRect();
      userInput({ stop: true });
      zoomAround(map.unproject([tap.x - r.left, tap.y - r.top]), 1, "dbltap");
      return;
    }
    lastTap = tap;
  };
  const onPointerCancel = (e: PointerEvent) => {
    touches.delete(e.pointerId);
    tapDown = null;
  };
  const onWheel = () => userInput({ stop: true, throttle: true });
  const onDblClick = (e: MapMouseEvent) => {
    // No preventDefault: MapLibre treats a prevented event as an active gesture and stops the camera
    // (our own zoom included). doubleClickZoom is off, so nothing else reacts to it. The zoom starts
    // after MapLibre has finished handling the event, for the same reason.
    if (performance.now() - lastDoubleTap < 500) return; // the browser's echo of a double-tap
    const at = e.lngLat;
    userInput({ stop: true });
    queueMicrotask(() => {
      if (!destroyed) zoomAround(at, 1, "dblclick");
    });
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    let dx = 0;
    let dy = 0;
    let dz = 0;
    switch (e.key) {
      case "ArrowLeft":
        dx = -100;
        break;
      case "ArrowRight":
        dx = 100;
        break;
      case "ArrowUp":
        dy = -100;
        break;
      case "ArrowDown":
        dy = 100;
        break;
      case "+":
      case "=":
      case "Add":
        dz = 1;
        break;
      case "-":
      case "_":
      case "Subtract":
        dz = -1;
        break;
      default:
        return;
    }
    e.preventDefault();
    userInput({ stop: true });
    const dur = reduced() ? 160 : DUR.calm;
    if (dz) zoomAround(map.getCenter(), dz, "key");
    else map.panBy([dx, dy], { duration: dur, easing: EASE.settle, essential: true }, { ldUser: "key" });
  };

  canvasBox.addEventListener("pointerdown", onPointerDown, { capture: true, passive: true });
  canvasBox.addEventListener("pointerup", onPointerUp, { capture: true, passive: true });
  canvasBox.addEventListener("pointercancel", onPointerCancel, { capture: true, passive: true });
  canvasBox.addEventListener("wheel", onWheel, { capture: true, passive: true });
  canvas.addEventListener("keydown", onKeyDown);
  map.on("dblclick", onDblClick);

  // Cursor: at rest the land is quiet (default); while the map is dragged, grabbing.
  const onDragStart = () => container.classList.add("is-grabbing");
  const onDragEnd = () => container.classList.remove("is-grabbing");
  map.on("dragstart", onDragStart);
  map.on("dragend", onDragEnd);

  /* ── store ────────────────────────────────────────────────────────── */

  const unsubStore = appStore.subscribe((s, prev) => {
    if (destroyed) return;
    if (s.insets !== prev.insets) insets = { ...s.insets };
    if (s.layers !== prev.layers) for (const k of FLAG_KEYS) if (s.layers[k] !== prev.layers[k]) applyFlag(k, s.layers[k]);
    if (s.layout !== prev.layout && s.layout !== layout) {
      layout = s.layout;
      lastMood = restyle(build(mood), 0);
    }
    if (s.mood !== prev.mood) {
      // Deferred: an explicit setMood(m, { duration }) in the same tick (the sunrise) takes precedence.
      queueMicrotask(() => {
        const m = getApp().mood;
        if (!destroyed && m !== mood) api.setMood(m);
      });
    }
  });

  /* ── API ──────────────────────────────────────────────────────────── */

  const notReady = (what: string): never => {
    throw new Error(`MapController.${what}() is available once ctrl.ready has resolved`);
  };

  const api: ControllerHandle = {
    ready,
    map: () => map,
    light: () => light ?? notReady("light"),
    markers: () => markers ?? notReady("markers"),
    overlays: () => overlays ?? notReady("overlays"),

    setMood(m: Mood, mo?: { duration?: number }): void {
      if (destroyed || !MOODS[m]) return;
      const duration = reduced() ? 160 : Math.max(0, mo?.duration ?? MOOD_TRANSITION_MS);
      if (m === mood) return;
      mood = m;
      applyCssVars(m);
      lastMood = restyle(build(m), duration);
      terrain?.setMood(m, duration);
      light?.setPalette(MOODS[m].light);
      overlays?.setMood(m);
      markers?.setMood(m);
    },
    getMood: () => mood,

    setLayerVisible(k: keyof LayerFlags, v: boolean): void {
      if (destroyed) return;
      // The LightLayer (routes, the lit site outline) follows the store: keep it the source of truth.
      if ((k === "routes" || k === "boundary") && getApp().layers[k] !== v) actions.toggleLayer(k, v);
      applyFlag(k, v);
    },

    jumpTo,
    easeTo: (t, mo) => move("ease", t, mo),
    flyTo: (t, mo) => move("fly", t, mo),
    stop(): void {
      if (!destroyed) map.stop();
    },
    homeTarget,
    regionTarget,

    getInsets: () => ({ ...insets }),
    setInsets(i: Insets): void {
      insets = { top: i.top, right: i.right, bottom: i.bottom, left: i.left };
    },
    onInteract(cb: () => void): () => void {
      interactCbs.add(cb);
      return () => interactCbs.delete(cb);
    },
    isMoving: () => !destroyed && (currentMove !== null || map.isMoving()),

    siteCenter: () => site,
    fadeContours(v, fo) {
      return terrain ? terrain.fadeContours(v, fo) : Promise.resolve(false);
    },
    debug(): ControllerDebug {
      const c = map.getCenter();
      return {
        mood,
        moving: api.isMoving(),
        zoom: map.getZoom(),
        center: [c.lng, c.lat],
        pitch: map.getPitch(),
        bearing: map.getBearing(),
        insets: { ...insets },
        demAttached: terrain?.demAttached ?? false,
        lastMood,
        lastMove,
        interrupts,
      };
    },

    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      rejectReady(new DOMException("The map was removed.", "AbortError"));
      sizeRo?.disconnect();
      prewarmAc.abort();
      offPrewarm();
      if (prewarmTimer) clearTimeout(prewarmTimer);
      unsubStore();
      offBusInterrupt();
      if (labelTimer) clearTimeout(labelTimer);
      for (const mv of [...pending]) {
        mv.cancelled = true;
        mv.finish();
      }
      canvasBox.removeEventListener("pointerdown", onPointerDown, { capture: true });
      canvasBox.removeEventListener("pointerup", onPointerUp, { capture: true });
      canvasBox.removeEventListener("pointercancel", onPointerCancel, { capture: true });
      canvasBox.removeEventListener("wheel", onWheel, { capture: true });
      canvas.removeEventListener("keydown", onKeyDown);
      interactCbs.clear();
      const safely = (f: () => void) => {
        try {
          f();
        } catch (err) {
          console.warn("[map] destroy", err);
        }
      };
      safely(() => markers?.destroy());
      safely(() => overlays?.destroy());
      safely(() => light?.destroy());
      safely(() => terrain?.destroy());
      markers = overlays = light = null;
      terrain = null;
      vignette.remove();
      container.classList.remove("ld-loc-map-host", "is-grabbing");
      for (const k of Object.keys(MOODS[mood].cssVars)) container.style.removeProperty(k);
      delete container.dataset.mood;
      safely(() => map.remove());
    },
  };

  return api;
}
